/**
 * GenericConfigPanel
 * Schema-driven control configuration panel
 * Renders any control config from schema definition
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Spin,
  Alert,
  Space,
  Form,
  Switch,
  Select,
  Slider,
  Radio,
  Checkbox,
  Input,
  InputNumber,
  Tag,
} from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';
import { FormSection, InfoTooltip, MultiSelect } from '../../../components/common';
import { getControlSchema } from './controlSchemas';
import { getControlPlugin } from './plugins';
import { spacing, colors } from '../../../theme/designTokens';

const { TextArea } = Input;

/**
 * Render a single form field based on schema
 */
function renderField(field, value, onChange) {
  const commonProps = {
    value,
    onChange: (val) => {
      // Handle different event types
      if (val?.target !== undefined) {
        // Input/TextArea
        onChange(val.target.value);
      } else if (typeof val === 'boolean') {
        // Switch/Checkbox
        onChange(val);
      } else {
        // Select/Slider/Radio
        onChange(val);
      }
    },
  };

  switch (field.type) {
    case 'checkbox':
      return (
        <div>
          <Switch
            checked={value}
            onChange={onChange}
          />
          <span style={{ marginLeft: spacing.sm }}>
            {field.label}
            {field.tooltip && <InfoTooltip content={field.tooltip} />}
          </span>
        </div>
      );

    case 'text':
      return (
        <div>
          {field.label && (
            <div style={{ marginBottom: spacing.xs }}>
              <strong>{field.label}</strong>
              {field.tooltip && <InfoTooltip content={field.tooltip} />}
            </div>
          )}
          <Input
            {...commonProps}
            placeholder={field.placeholder}
            disabled={field.disabled}
          />
        </div>
      );

    case 'textarea':
      return (
        <div>
          {field.label && (
            <div style={{ marginBottom: spacing.xs }}>
              <strong>{field.label}</strong>
              {field.tooltip && <InfoTooltip content={field.tooltip} />}
            </div>
          )}
          <TextArea
            {...commonProps}
            rows={field.rows || 4}
            placeholder={field.placeholder}
            disabled={field.disabled}
          />
        </div>
      );

    case 'number':
      return (
        <div>
          {field.label && (
            <div style={{ marginBottom: spacing.xs }}>
              <strong>{field.label}</strong>
              {field.tooltip && <InfoTooltip content={field.tooltip} />}
            </div>
          )}
          <InputNumber
            {...commonProps}
            min={field.min}
            max={field.max}
            step={field.step || 1}
            style={{ width: '100%' }}
            disabled={field.disabled}
          />
        </div>
      );

    case 'select':
      // Render as radio buttons for better UX and to avoid dropdown styling issues
      return (
        <div>
          {field.label && (
            <div style={{ marginBottom: spacing.xs }}>
              <strong>{field.label}</strong>
              {field.tooltip && <InfoTooltip content={field.tooltip} />}
            </div>
          )}
          <Radio.Group
            {...commonProps}
            options={field.options?.map(opt => ({
              label: opt.description ? (
                <div>
                  <div>{opt.label}</div>
                  <div style={{
                    fontSize: 12,
                    color: 'rgba(23, 5, 43, 0.55)',
                    marginTop: 2,
                    fontWeight: 400
                  }}>
                    {opt.description}
                  </div>
                </div>
              ) : opt.label,
              value: opt.value,
            }))}
            disabled={field.disabled}
          />
        </div>
      );

    case 'multiselect':
      return (
        <div>
          {field.label && (
            <div style={{ marginBottom: spacing.xs }}>
              <strong>{field.label}</strong>
              {field.tooltip && <InfoTooltip content={field.tooltip} />}
            </div>
          )}
          <MultiSelect
            value={value || []}
            onChange={onChange}
            options={field.options}
            placeholder={field.placeholder}
            showSelectAll
            style={{ width: '100%' }}
          />
        </div>
      );

    case 'slider':
      return (
        <div>
          {field.label && (
            <div style={{ marginBottom: spacing.xs }}>
              <strong>{field.label}</strong>
              {field.tooltip && <InfoTooltip content={field.tooltip} />}
            </div>
          )}
          <Slider
            {...commonProps}
            min={field.min || 0}
            max={field.max || 100}
            step={field.step || 1}
            marks={field.marks}
            tooltip={{ formatter: (val) => `${val}${field.unit || ''}` }}
            disabled={field.disabled}
          />
        </div>
      );

    case 'radio':
      return (
        <div>
          {field.label && (
            <div style={{ marginBottom: spacing.xs }}>
              <strong>{field.label}</strong>
              {field.tooltip && <InfoTooltip content={field.tooltip} />}
            </div>
          )}
          <Radio.Group
            {...commonProps}
            options={field.options?.map(opt => ({
              label: opt.description ? (
                <div>
                  <div>{opt.label}</div>
                  <div style={{
                    fontSize: 12,
                    color: 'rgba(23, 5, 43, 0.55)',
                    marginTop: 2,
                    fontWeight: 400
                  }}>
                    {opt.description}
                  </div>
                </div>
              ) : opt.label,
              value: opt.value,
            }))}
            disabled={field.disabled}
          />
        </div>
      );

    case 'tags':
      return (
        <div>
          {field.label && (
            <div style={{ marginBottom: spacing.xs }}>
              <strong>{field.label}</strong>
              {field.tooltip && <InfoTooltip content={field.tooltip} />}
            </div>
          )}
          <Select
            mode="tags"
            value={value || []}
            onChange={onChange}
            placeholder={field.placeholder}
            style={{ width: '100%' }}
            disabled={field.disabled}
          />
        </div>
      );

    default:
      return <div>Unsupported field type: {field.type}</div>;
  }
}

/**
 * GenericConfigPanel Component
 */
function GenericConfigPanel({ projectId, controlCode, customPlugin, usePlugin = true }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const schema = getControlSchema(controlCode);

  // Auto-detect plugin if not explicitly provided
  const plugin = customPlugin || (usePlugin ? getControlPlugin(controlCode) : null);

  useEffect(() => {
    if (projectId && controlCode) {
      fetchConfig();
    }
  }, [projectId, controlCode]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await getControlConfig(projectId, controlCode);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
      // Use default config from schema
      if (schema) {
        setConfig(schema.defaultConfig);
      } else {
        setConfig({ enabled: false });
      }
      if (error.response && error.response.status !== 404) {
        showError('Failed to load configuration. Using defaults.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveControlConfig(projectId, controlCode, config);
      showSuccess('Configuration saved successfully');
      setHasChanges(false);
      fetchConfig();
    } catch (error) {
      console.error('Failed to save config:', error);
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key, value) => {
    // Handle nested keys (e.g., 'masking_strategy.email')
    if (key.includes('.')) {
      const parts = key.split('.');
      const newConfig = { ...config };
      let current = newConfig;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
      setConfig(newConfig);
    } else {
      setConfig({ ...config, [key]: value });
    }
    setHasChanges(true);
  };

  const getFieldValue = (key) => {
    if (key.includes('.')) {
      const parts = key.split('.');
      let current = config;
      for (const part of parts) {
        if (!current) return undefined;
        current = current[part];
      }
      return current;
    }
    return config?.[key];
  };

  if (loading) {
    return (
      <Card>
        <Spin tip="Loading configuration..." />
      </Card>
    );
  }

  // If plugin exists, use it (plugins internally render GenericConfigPanel)
  if (plugin) {
    const Plugin = plugin;
    return <Plugin projectId={projectId} controlCode={controlCode} config={config} />;
  }

  // If no schema, show fallback
  if (!schema) {
    return (
      <Card>
        <Alert
          type="info"
          message="Configuration not available"
          description={`Schema for ${controlCode} has not been defined yet. Using legacy config panel.`}
        />
      </Card>
    );
  }

  return (
    <Card
      title={schema.controlName}
      extra={
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchConfig}
            type="text"
          >
            Reset
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!hasChanges}
          >
            Save Changes
          </Button>
        </Space>
      }
    >
      {schema.sections.map((section, index) => (
        <div
          key={section.key}
          style={{
            marginTop: index === 0 ? 0 : 24,
            paddingTop: index === 0 ? 0 : 24,
            borderTop: index === 0 ? 'none' : `1px solid ${colors.border.light}`
          }}
        >
          {/* Bar-level heading */}
          <div style={{
            fontSize: 15.5,
            fontWeight: 600,
            color: '#17052B',
            marginBottom: 8
          }}>
            {section.title}
          </div>

          {/* Fields with proper spacing */}
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {section.fields.map((field) => (
              <div key={field.key}>
                {renderField(
                  field,
                  getFieldValue(field.key),
                  (value) => updateField(field.key, value)
                )}
              </div>
            ))}
          </Space>
        </div>
      ))}

      {hasChanges && (
        <Alert
          type="warning"
          message="Unsaved Changes"
          description="You have unsaved configuration changes. Click 'Save Changes' to apply them."
          style={{ marginTop: spacing.lg }}
          showIcon
        />
      )}
    </Card>
  );
}

export default GenericConfigPanel;
